import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { uploadFiles, errMsg } from "@/lib/api";
import { ImagePlus, Trash2, Loader2, Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered, Link2, RemoveFormatting, AlignLeft, AlignCenter, AlignRight, AlignJustify, Pilcrow } from "lucide-react";

const TOOLS = [
  { icon: Bold, cmd: "bold", label: "Bold" },
  { icon: Italic, cmd: "italic", label: "Italic" },
  { icon: Underline, cmd: "underline", label: "Underline" },
  { icon: Heading2, cmd: "formatBlock", arg: "H2", label: "Heading" },
  { icon: Heading3, cmd: "formatBlock", arg: "H3", label: "Subheading" },
  { icon: Pilcrow, cmd: "formatBlock", arg: "P", label: "Paragraph" },
  { icon: List, cmd: "insertUnorderedList", label: "Bulleted list" },
  { icon: ListOrdered, cmd: "insertOrderedList", label: "Numbered list" },
  { icon: Link2, cmd: "createLink", label: "Link" },
  { icon: RemoveFormatting, cmd: "removeFormat", label: "Clear formatting" },
  { icon: AlignLeft, cmd: "justifyLeft", label: "Align left" },
  { icon: AlignCenter, cmd: "justifyCenter", label: "Align center" },
  { icon: AlignRight, cmd: "justifyRight", label: "Align right" },
  { icon: AlignJustify, cmd: "justifyFull", label: "Justify" },
];

export const RichEditor = ({ value, onChange, testId = "rich-editor", placeholder = "Start typing…" }) => {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const rangeRef = useRef(null);
  const [selImg, setSelImg] = useState(null);
  const [imgWidth, setImgWidth] = useState(100);
  const [busy, setBusy] = useState(false);

  const saveRange = () => {
    const sel = window.getSelection();
    if (sel.rangeCount && ref.current.contains(sel.anchorNode)) rangeRef.current = sel.getRangeAt(0).cloneRange();
  };

  const insertImage = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const [doc] = await uploadFiles(files);
      ref.current.focus();
      const sel = window.getSelection();
      if (rangeRef.current) { sel.removeAllRanges(); sel.addRange(rangeRef.current); }
      document.execCommand("insertHTML", false, `<p><img src="${doc.url}" alt="" style="width:100%;height:auto;display:block;margin:0 auto;"></p>`);
      onChange(ref.current.innerHTML);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const pickImage = (e) => {
    if (e.target.tagName === "IMG") {
      setSelImg(e.target);
      setImgWidth(parseInt(e.target.style.width, 10) || 100);
    } else setSelImg(null);
  };

  const resizeImage = (v) => {
    setImgWidth(v);
    Object.assign(selImg.style, { width: `${v}%`, height: "auto", display: "block", margin: "0 auto" });
    onChange(ref.current.innerHTML);
  };

  const removeImage = () => {
    selImg.remove();
    setSelImg(null);
    onChange(ref.current.innerHTML);
  };

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) ref.current.innerHTML = value || "";
  }, [value]);

  const run = (t) => {
    ref.current.focus();
    if (t.cmd === "createLink") {
      const url = window.prompt("Link URL", "https://");
      if (!url) return;
      document.execCommand("createLink", false, url);
    } else if (t.cmd === "removeFormat") {
      document.execCommand("removeFormat");
      document.execCommand("unlink");
      document.execCommand("formatBlock", false, "P");
    } else {
      document.execCommand(t.cmd, false, t.arg);
    }
    onChange(ref.current.innerHTML);
  };

  return (
    <div className="rounded-lg border border-input bg-white" data-testid={testId}>
      <div className="flex flex-wrap gap-0.5 border-b border-input bg-muted/50 p-1.5 rounded-t-lg">
        {TOOLS.map((t) => (
          <button key={t.label} type="button" title={t.label} aria-label={t.label} onMouseDown={(e) => { e.preventDefault(); run(t); }}
            className="h-8 w-8 rounded flex items-center justify-center text-charcoal/70 hover:bg-white hover:text-brand" data-testid={`${testId}-${t.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <t.icon className="h-4 w-4" />
          </button>
        ))}
        <button type="button" title="Insert image" aria-label="Insert image" disabled={busy} onMouseDown={(e) => { e.preventDefault(); saveRange(); fileRef.current.click(); }}
          className="h-8 w-8 rounded flex items-center justify-center text-charcoal/70 hover:bg-white hover:text-brand" data-testid={`${testId}-insert-image`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
        </button>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => { insertImage(e.target.files); e.target.value = ""; }} data-testid={`${testId}-image-file`} />
      </div>
      {selImg && (
        <div className="flex flex-wrap items-center gap-3 border-b border-input bg-ivory px-3 py-2" data-testid={`${testId}-image-size-bar`}>
          <span className="text-xs font-semibold">Image size</span>
          <Slider value={[imgWidth]} min={10} max={100} step={5} onValueChange={([v]) => resizeImage(v)} className="flex-1 max-w-xs" data-testid={`${testId}-image-size-slider`} />
          <span className="w-12 text-xs font-mono" data-testid={`${testId}-image-size-value`}>{imgWidth}%</span>
          <button type="button" onClick={removeImage} className="flex items-center gap-1 text-xs text-destructive" data-testid={`${testId}-image-remove`}><Trash2 className="h-3.5 w-3.5" />Remove</button>
        </div>
      )}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={() => onChange(ref.current.innerHTML)}
        onClick={pickImage}
        onKeyUp={saveRange}
        onMouseUp={saveRange}
        className="rich-editor rich-content min-h-[140px] max-h-[380px] overflow-y-auto thin-scroll p-4 text-sm outline-none"
        data-testid={`${testId}-area`}
      />
    </div>
  );
};
